import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { LinkSquare02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { ApplicationStatus } from "shared/types";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { boomFinanceApi } from "api/boom-finance";
import {
  SUBMIT_BOOM_FINANCE_APPS_TRIGGER_SETTING,
  SUBMIT_BOOM_FINANCE_ON_APPROVAL
} from "api/boom-finance/constants";
import { QUERY_SETTINGS, getSettings } from "api/landlord/landlord";
import { getErrorMessage } from "utils/error";

interface Props {
  applicationStatus: ApplicationStatus;
  boomFinanceExternalId?: string | null;
}

export const BoomFinanceLinkButton = ({
  applicationStatus,
  boomFinanceExternalId
}: Props) => {
  const { data: settingsData } = useQuery({
    queryKey: [QUERY_SETTINGS],
    queryFn: getSettings
  });

  const trigger = settingsData?.settings?.find(
    (setting) => setting.name === SUBMIT_BOOM_FINANCE_APPS_TRIGGER_SETTING
  )?.value;
  // The backend default is on_review, and ListPublicSettings returns the raw
  // (possibly null) stored value — so treat anything that isn't an explicit
  // on_approval as "review before sending".
  const isReviewBeforeSending = trigger !== SUBMIT_BOOM_FINANCE_ON_APPROVAL;

  const { mutate: openInLenderPortal, isPending } = useMutation({
    mutationKey: ["BOOM_FINANCE_MAGIC_LINK", "CREATE"],
    mutationFn: () =>
      boomFinanceApi.createMagicLink({
        redirectTo: `/applications/${boomFinanceExternalId}`
      }),
    onSuccess: ({ url }) => {
      window.open(url, "_blank", "noopener,noreferrer");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  if (!isReviewBeforeSending) return null;

  const isApproved = applicationStatus === ApplicationStatus.Approved;
  const canSubmit = isApproved && Boolean(boomFinanceExternalId);

  return (
    <Button
      variant={ButtonVariant.BASE}
      size={ButtonSize.SM}
      HugeIcon={LinkSquare02Icon}
      iconPosition="right"
      onClick={() => openInLenderPortal()}
      disabled={!canSubmit || isPending}
      loading={isPending}
      tooltip={
        isApproved
          ? undefined
          : "Approve the application before sending it to the lender."
      }
      tooltipZIndex={21}
    >
      Open BoomFinance
    </Button>
  );
};
